// v3.13.0 — Tailored Capability Statement generator (RFI pursuits).
//
// Single-page workflow:
//   1) Gate: only RFI-equivalent pursuits can generate. The gate reads
//      `procurement_phase` (FAR Lifecycle Phase) — `SOURCES_SOUGHT` or
//      `SPECIAL_NOTICE` (the two pre-solicitation phases that legitimately
//      invite a Capability Statement). If neither, we surface a discoverable
//      empty state with a deep-link to the FAR phase tracker on Intake.
//   2) Generate button → modal with two tabs:
//        - "Auto-pick" (default): system selects top-N library matches.
//        - "Choose records": full picker over PPs + Certs + KP + Winning Proposals.
//   3) After generation: in-page preview of the structured sections
//      + Download DOCX + Regenerate.

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  FileText,
  Sparkles,
  Download,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Award,
  Briefcase,
  Trophy,
  Users,
  ChevronRight,
  Building2,
  Pencil,
  Save,
  X,
  Plus,
  Trash2,
} from 'lucide-react';
import PursuitNextStepFooter from '../../components/pursuit/PursuitNextStepFooter';
import PursuitLifecycleStepper from '../../components/pursuit/PursuitLifecycleStepper';
import PursuitPageShell from '../../components/pursuit/PursuitPageShell';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_ICONS = {
  past_performances: Briefcase,
  certifications: Award,
  key_personnel: Users,
  winning_proposals: Trophy,
};

const SECTION_LABELS = {
  past_performances: 'Past Performance',
  certifications: 'Certifications',
  key_personnel: 'Key Personnel',
  winning_proposals: 'Winning Proposals',
};

function _scoreColor(score) {
  if (score >= 60) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (score >= 30) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

function CapabilityStatementPage() {
  const { pursuitId, workspaceId } = useParams();
  const { authToken } = useAuth();

  const [pursuit, setPursuit] = useState(null);
  const [statement, setStatement] = useState(null);  // persisted record
  const [loading, setLoading] = useState(true);

  const [genOpen, setGenOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  // v3.14.1 — RFI-equivalent gate reads from FAR Lifecycle Phase, not
  // the redundant `notice_type` field. SOURCES_SOUGHT (FAR 5.204 /
  // 15.201) and SPECIAL_NOTICE (FAR 5.205) both legitimately invite a
  // Capability Statement response. Falls back to legacy notice_type=RFI
  // for backward-compat with v3.13.0 data.
  const phase = (pursuit?.procurement_phase || '').toUpperCase();
  const legacyNotice = (pursuit?.notice_type || '').toUpperCase();
  const isRFI = phase === 'SOURCES_SOUGHT' || phase === 'SPECIAL_NOTICE' || legacyNotice === 'RFI';

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  }), [authToken]);

  const loadAll = useCallback(async () => {
    if (!pursuitId) return;
    setLoading(true);
    try {
      const [pRes, sRes] = await Promise.all([
        fetch(`${API}/api/pursuits/${pursuitId}`, { headers, credentials: 'include' }),
        fetch(`${API}/api/pursuits/${pursuitId}/capability-statement`, {
          headers, credentials: 'include',
        }),
      ]);
      if (pRes.ok) setPursuit(await pRes.json());
      if (sRes.ok) {
        const s = await sRes.json();
        setStatement(s.exists ? s : null);
      }
    } catch (e) {
      toast.error('Failed to load Capability Statement');
    } finally {
      setLoading(false);
    }
  }, [pursuitId, headers]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleDownload = async () => {
    try {
      const res = await fetch(`${API}/api/pursuits/${pursuitId}/capability-statement/docx`, {
        headers, credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Download failed');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : `CapabilityStatement_${pursuitId}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const handleGenerated = (record) => {
    setStatement(record);
    setGenOpen(false);
    toast.success('Capability Statement generated');
  };

  // v3.13.1 — Save writer edits back to the persisted record.
  // Optimistically updates the in-memory statement after server ack so
  // Download DOCX always picks up the latest sections.
  const handleSaveEdits = async (nextSections) => {
    try {
      const res = await fetch(
        `${API}/api/pursuits/${pursuitId}/capability-statement`,
        {
          method: 'PATCH',
          headers,
          credentials: 'include',
          body: JSON.stringify({ sections: nextSections }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
        return false;
      }
      const data = await res.json();
      setStatement(prev => ({ ...prev, sections: data.sections, edited_at: data.edited_at }));
      toast.success('Edits saved');
      return true;
    } catch (e) {
      toast.error('Save failed');
      return false;
    }
  };

  if (loading) {
    return (
      <PursuitPageShell
        title="Capability Statement"
        titleIcon={Sparkles}
        showStepper={false}
        testId="cap-stmt-loading"
      >
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </PursuitPageShell>
    );
  }

  // RFI gate
  if (!isRFI) {
    return (
      <PursuitPageShell
        eyebrow={<PageEyebrow pursuit={pursuit} />}
        title="Capability Statement"
        subtitle={pursuit?.opportunity_name || 'Tailored RFI response'}
        testId="cap-stmt-shell-not-rfi"
      >
        <Card className="border-amber-200 bg-amber-50/50" data-testid="cap-stmt-not-rfi">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-14 h-14 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-amber-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Capability Statements are for pre-solicitation phases
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                A tailored Capability Statement is the standard contractor response when an agency
                publishes a <strong>Sources Sought (RFI)</strong> or <strong>Special Notice</strong>.
                Set this pursuit&rsquo;s <em>FAR Lifecycle Phase</em> to{' '}
                <code className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Sources Sought (RFI)</code>{' '}
                or{' '}
                <code className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Special Notice</code>{' '}
                on the FAR phase tracker at the top of Opportunity Intake to enable the generator.
              </p>
              {pursuit?.procurement_phase && (
                <p className="text-xs text-slate-500 mt-2">
                  Current FAR phase:{' '}
                  <Badge variant="outline" className="font-mono">
                    {pursuit.procurement_phase.replace(/_/g, ' ')}
                  </Badge>
                </p>
              )}
            </div>
            <Button asChild data-testid="cap-stmt-go-to-intake">
              <Link to={`/${workspaceId}/pursuit/${pursuitId}/intake`}>
                Go to Opportunity Intake
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </PursuitPageShell>
    );
  }

  return (
    <PursuitPageShell
      eyebrow={<PageEyebrow pursuit={pursuit} />}
      title="Capability Statement"
      subtitle={pursuit?.opportunity_name || 'Tailored RFI response'}
      nextStepFor="capability_statement"
      testId="cap-stmt-shell"
    >
      {!statement && (
        <Card className="border-slate-200" data-testid="cap-stmt-empty">
          <CardContent className="p-10 text-center space-y-4">
            <div className="w-14 h-14 mx-auto bg-violet-100 rounded-full flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-violet-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Generate a tailored Capability Statement
              </h3>
              <p className="text-sm text-slate-600 mt-1 max-w-xl mx-auto">
                We&rsquo;ll weave your prior Past Performances, Certifications, Key Personnel,
                and Winning Proposals into a federal-style 1&ndash;2 page response to this RFI.
              </p>
            </div>
            <Button
              onClick={() => setGenOpen(true)}
              className="bg-violet-700 hover:bg-violet-800"
              data-testid="cap-stmt-generate-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Capability Statement
            </Button>
          </CardContent>
        </Card>
      )}

      {statement && (
        <StatementPreview
          statement={statement}
          onDownload={handleDownload}
          onRegenerate={() => setGenOpen(true)}
          onSaveEdits={handleSaveEdits}
        />
      )}

      {genOpen && (
        <GenerateDialog
          open={genOpen}
          onClose={() => setGenOpen(false)}
          pursuitId={pursuitId}
          workspaceId={workspaceId}
          headers={headers}
          onGenerated={handleGenerated}
        />
      )}
    </PursuitPageShell>
  );
}

// Eyebrow rendered inside PursuitPageShell — replaces the old PageHeader
// component that wrapped title + subtitle + agency badge in one block.
function PageEyebrow({ pursuit }) {
  const phaseLabel = pursuit?.procurement_phase
    ? pursuit.procurement_phase.replace(/_/g, ' ')
    : null;
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500">
      <Building2 className="w-3.5 h-3.5" />
      <span>{pursuit?.agency_name || 'Federal Agency'}</span>
      {phaseLabel && (
        <Badge variant="outline" className="ml-1 font-mono text-[10px]">{phaseLabel}</Badge>
      )}
    </div>
  );
}

function _OldPageHeader_unused({ pursuit }) {
  // Removed in v3.18.19 — replaced by PursuitPageShell + PageEyebrow.
  return null;
}

// ---------------------------------------------------------------------------
// Preview of the persisted Capability Statement — read-only and edit modes.
// In edit mode each section becomes a controlled input; Save persists via
// PATCH, Cancel reverts to the last server-confirmed state.
// ---------------------------------------------------------------------------

function StatementPreview({ statement, onDownload, onRegenerate, onSaveEdits }) {
  const s = statement.sections || {};
  const company = statement.company || {};
  const generatedAt = statement.generated_at
    ? new Date(statement.generated_at).toLocaleString()
    : '';
  const editedAt = statement.edited_at
    ? new Date(statement.edited_at).toLocaleString()
    : '';

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(s);
  const [saving, setSaving] = useState(false);

  // When server-side sections change (after generate/save), re-sync draft.
  useEffect(() => { setDraft(statement.sections || {}); }, [statement.sections]);

  const startEdit = () => { setDraft(statement.sections || {}); setEditing(true); };
  const cancelEdit = () => { setDraft(statement.sections || {}); setEditing(false); };

  const handleSave = async () => {
    setSaving(true);
    const ok = await onSaveEdits(draft);
    setSaving(false);
    if (ok) setEditing(false);
  };

  const set = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));

  return (
    <Card className="border-slate-200" data-testid="cap-stmt-preview">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {company.name || 'Capability Statement'}
          </CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">
            Generated {generatedAt}
            {statement.mode && (
              <Badge variant="outline" className="ml-2 text-[10px]">
                {statement.mode === 'auto' ? 'Auto-picked' : 'Curated'}
              </Badge>
            )}
            {editedAt && (
              <span className="ml-2 text-slate-400">· edited {editedAt}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!editing ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={startEdit}
                data-testid="cap-stmt-edit-btn"
              >
                <Pencil className="w-4 h-4 mr-1.5" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onRegenerate}
                data-testid="cap-stmt-regenerate-btn"
              >
                <RefreshCw className="w-4 h-4 mr-1.5" />
                Regenerate
              </Button>
              <Button
                size="sm"
                onClick={onDownload}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="cap-stmt-download-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Download DOCX
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={cancelEdit}
                disabled={saving}
                data-testid="cap-stmt-cancel-edit-btn"
              >
                <X className="w-4 h-4 mr-1.5" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-violet-700 hover:bg-violet-800"
                data-testid="cap-stmt-save-edit-btn"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1.5" />
                    Save Changes
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <IdentityStrip company={company} statement={statement} />

        <ParagraphField
          heading="Executive Summary"
          value={editing ? draft.executive_summary : s.executive_summary}
          onChange={(v) => set('executive_summary', v)}
          editing={editing}
          testid="exec-summary"
        />

        <ParagraphField
          heading="NAICS / Scope Alignment"
          value={editing ? draft.naics_alignment : s.naics_alignment}
          onChange={(v) => set('naics_alignment', v)}
          editing={editing}
          testid="naics-alignment"
        />

        <BulletListField
          heading="Core Capabilities"
          items={editing ? (draft.core_capabilities || []) : (s.core_capabilities || [])}
          onChange={(arr) => set('core_capabilities', arr)}
          editing={editing}
          testid="core-capabilities"
          placeholder="Capability bullet…"
        />

        <BulletListField
          heading="Differentiators"
          items={editing ? (draft.differentiators || []) : (s.differentiators || [])}
          onChange={(arr) => set('differentiators', arr)}
          editing={editing}
          testid="differentiators"
          placeholder="Differentiator…"
        />

        <PastPerformanceField
          items={editing ? (draft.past_performance_highlights || []) : (s.past_performance_highlights || [])}
          onChange={(arr) => set('past_performance_highlights', arr)}
          editing={editing}
        />

        <KeyPersonnelField
          items={editing ? (draft.key_personnel_highlights || []) : (s.key_personnel_highlights || [])}
          onChange={(arr) => set('key_personnel_highlights', arr)}
          editing={editing}
        />

        <CertificationsField
          items={editing ? (draft.certifications || []) : (s.certifications || [])}
          onChange={(arr) => set('certifications', arr)}
          editing={editing}
        />

        <ParagraphField
          heading="Closing"
          value={editing ? draft.closing : s.closing}
          onChange={(v) => set('closing', v)}
          editing={editing}
          italic
          testid="closing"
        />
      </CardContent>
    </Card>
  );
}

// ── Reusable section primitives ───────────────────────────────────────────

function ParagraphField({ heading, value, onChange, editing, italic, testid }) {
  if (!editing && !value) return null;
  return (
    <Section heading={heading}>
      {editing ? (
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="text-sm"
          data-testid={`cap-stmt-edit-${testid}`}
        />
      ) : (
        <p
          className={`text-sm text-slate-700 leading-relaxed ${italic ? 'italic pt-2 border-t border-slate-100' : ''}`}
        >
          {value}
        </p>
      )}
    </Section>
  );
}

function BulletListField({ heading, items, onChange, editing, testid, placeholder }) {
  if (!editing && items.length === 0) return null;
  const updateAt = (i, v) => onChange(items.map((b, idx) => (idx === i ? v : b)));
  const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, '']);
  return (
    <Section heading={heading}>
      {editing ? (
        <div className="space-y-2">
          {items.map((b, i) => (
            <div key={`${testid}-${i}`} className="flex items-start gap-2">
              <Input
                value={b}
                placeholder={placeholder}
                onChange={(e) => updateAt(i, e.target.value)}
                className="flex-1 text-sm"
                data-testid={`cap-stmt-edit-${testid}-${i}`}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeAt(i)}
                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-2"
                data-testid={`cap-stmt-edit-${testid}-remove-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={add}
            className="text-violet-700 border-violet-200 hover:bg-violet-50"
            data-testid={`cap-stmt-edit-${testid}-add`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add bullet
          </Button>
        </div>
      ) : (
        <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
          {items.map((b, i) => <li key={`${testid}-${i}`}>{b}</li>)}
        </ul>
      )}
    </Section>
  );
}

function PastPerformanceField({ items, onChange, editing }) {
  if (!editing && items.length === 0) return null;
  const updateAt = (i, key, value) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)));
  const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () =>
    onChange([...items, { title: '', agency: '', contract_value: '', period: '', relevance: '', outcomes: '' }]);

  return (
    <Section heading="Relevant Past Performance">
      <div className="space-y-3">
        {items.map((pp, i) => (
          editing ? (
            <div key={`pp-${i}`} className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50/40">
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={pp.title || ''}
                  placeholder="Title"
                  onChange={(e) => updateAt(i, 'title', e.target.value)}
                  className="text-sm"
                  data-testid={`cap-stmt-edit-pp-${i}-title`}
                />
                <Input
                  value={pp.agency || ''}
                  placeholder="Agency"
                  onChange={(e) => updateAt(i, 'agency', e.target.value)}
                  className="text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={pp.contract_value || ''}
                  placeholder="Contract value (e.g. $1.5M)"
                  onChange={(e) => updateAt(i, 'contract_value', e.target.value)}
                  className="text-sm"
                />
                <Input
                  value={pp.period || ''}
                  placeholder="Period (e.g. 2023-01 to 2024-12)"
                  onChange={(e) => updateAt(i, 'period', e.target.value)}
                  className="text-sm"
                />
              </div>
              <Textarea
                value={pp.relevance || ''}
                placeholder="Why this PP matters for the RFI"
                rows={2}
                onChange={(e) => updateAt(i, 'relevance', e.target.value)}
                className="text-sm"
              />
              <Textarea
                value={pp.outcomes || ''}
                placeholder="Outcomes / CPARS / accomplishments"
                rows={2}
                onChange={(e) => updateAt(i, 'outcomes', e.target.value)}
                className="text-sm"
              />
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeAt(i)}
                  className="text-rose-600 hover:bg-rose-50"
                  data-testid={`cap-stmt-edit-pp-${i}-remove`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div key={`pp-${i}`} className="border-l-2 border-violet-300 pl-3 py-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-medium text-slate-900 text-sm">{pp.title}</span>
                {pp.agency && <span className="text-xs text-slate-500">· {pp.agency}</span>}
                {pp.contract_value && <span className="text-xs text-slate-500">· {pp.contract_value}</span>}
                {pp.period && <span className="text-xs text-slate-500">· {pp.period}</span>}
              </div>
              {pp.relevance && <p className="text-sm text-slate-700 mt-0.5">{pp.relevance}</p>}
              {pp.outcomes && (
                <p className="text-xs text-slate-600 mt-0.5"><strong>Outcomes:</strong> {pp.outcomes}</p>
              )}
            </div>
          )
        ))}
        {editing && (
          <Button
            variant="outline"
            size="sm"
            onClick={add}
            className="text-violet-700 border-violet-200 hover:bg-violet-50"
            data-testid="cap-stmt-edit-pp-add"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Past Performance
          </Button>
        )}
      </div>
    </Section>
  );
}

function KeyPersonnelField({ items, onChange, editing }) {
  if (!editing && items.length === 0) return null;
  const updateAt = (i, key, value) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)));
  const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { name: '', title: '', highlight: '' }]);

  return (
    <Section heading="Key Personnel">
      {editing ? (
        <div className="space-y-2">
          {items.map((kp, i) => (
            <div key={`kp-${i}`} className="border border-slate-200 rounded-md p-2 space-y-2 bg-slate-50/40">
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={kp.name || ''}
                  placeholder="Name"
                  onChange={(e) => updateAt(i, 'name', e.target.value)}
                  className="text-sm"
                  data-testid={`cap-stmt-edit-kp-${i}-name`}
                />
                <Input
                  value={kp.title || ''}
                  placeholder="Title"
                  onChange={(e) => updateAt(i, 'title', e.target.value)}
                  className="text-sm"
                />
              </div>
              <Textarea
                value={kp.highlight || ''}
                placeholder="Why they're relevant to the RFI"
                rows={2}
                onChange={(e) => updateAt(i, 'highlight', e.target.value)}
                className="text-sm"
              />
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeAt(i)}
                  className="text-rose-600 hover:bg-rose-50"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={add}
            className="text-violet-700 border-violet-200 hover:bg-violet-50"
            data-testid="cap-stmt-edit-kp-add"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Key Personnel
          </Button>
        </div>
      ) : (
        <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
          {items.map((kp, i) => (
            <li key={`kp-${i}`}>
              <strong>{kp.name}</strong>
              {kp.title && <>, {kp.title}</>}
              {kp.highlight && <> — {kp.highlight}</>}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function CertificationsField({ items, onChange, editing }) {
  if (!editing && items.length === 0) return null;
  const updateAt = (i, v) => onChange(items.map((c, idx) => (idx === i ? v : c)));
  const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, '']);
  return (
    <Section heading="Certifications & Designations">
      {editing ? (
        <div className="space-y-2">
          {items.map((c, i) => (
            <div key={`c-${i}`} className="flex items-center gap-2">
              <Input
                value={c}
                placeholder="Certification (Issuer, exp YYYY)"
                onChange={(e) => updateAt(i, e.target.value)}
                className="flex-1 text-sm"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeAt(i)}
                className="text-rose-600 hover:bg-rose-50 px-2"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={add}
            className="text-violet-700 border-violet-200 hover:bg-violet-50"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add certification
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((c, i) => (
            <Badge key={`c-${i}`} variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              {c}
            </Badge>
          ))}
        </div>
      )}
    </Section>
  );
}

function IdentityStrip({ company, statement }) {
  const bits = [];
  if (company.uei) bits.push(`UEI: ${company.uei}`);
  if (company.cage_code) bits.push(`CAGE: ${company.cage_code}`);
  if (statement?.rfi_snapshot?.naics_code) bits.push(`NAICS: ${statement.rfi_snapshot.naics_code}`);
  if (company.socioeconomic_status) bits.push(company.socioeconomic_status);
  if (bits.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 text-xs text-slate-600 border border-slate-200 bg-slate-50 rounded-md px-3 py-2">
      {bits.map((b, i) => <span key={`id-${i}`}>{b}</span>)}
    </div>
  );
}

function Section({ heading, children }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900 mb-1.5 flex items-center gap-1.5">
        <FileText className="w-3.5 h-3.5 text-violet-600" />
        {heading}
      </h3>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generate dialog (Auto / Directed)
// ---------------------------------------------------------------------------

function GenerateDialog({ open, onClose, pursuitId, workspaceId, headers, onGenerated }) {
  const [tab, setTab] = useState('auto');
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [picked, setPicked] = useState({
    past_performance_ids: [],
    certification_ids: [],
    key_personnel_ids: [],
    winning_proposal_ids: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/api/pursuits/${pursuitId}/capability-statement/library`,
          { headers, credentials: 'include' },
        );
        if (!res.ok) {
          if (!cancelled) toast.error('Failed to load library options');
          return;
        }
        const data = await res.json();
        if (!cancelled) setLibrary(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pursuitId, headers]);

  const totalSelected = (picked.past_performance_ids.length
    + picked.certification_ids.length
    + picked.key_personnel_ids.length
    + picked.winning_proposal_ids.length);

  const togglePick = (key, id) => {
    setPicked(prev => {
      const set = new Set(prev[key]);
      set.has(id) ? set.delete(id) : set.add(id);
      return { ...prev, [key]: Array.from(set) };
    });
  };

  const onGenerate = async () => {
    setGenerating(true);
    const body = tab === 'auto'
      ? { mode: 'auto', auto_top_n: 3 }
      : { mode: 'directed', selections: picked };
    try {
      const res = await fetch(
        `${API}/api/pursuits/${pursuitId}/capability-statement/generate`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : err.detail?.message || 'Generation failed');
        return;
      }
      const record = await res.json();
      onGenerated(record);
    } catch (e) {
      toast.error('Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const directedReady = totalSelected > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col" data-testid="cap-stmt-gen-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-700" />
            Generate Capability Statement
          </DialogTitle>
          <DialogDescription>
            Choose how to source the prior assets that feed the AI.
          </DialogDescription>
        </DialogHeader>

        {!loading && library?.requirements_matrix?.work_scope_document_available && !library.requirements_matrix.built && (
          <div
            className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800"
            data-testid="cap-stmt-rtm-warning"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
            <div>
              <p className="font-medium">Requirements matrix hasn&rsquo;t been built yet</p>
              <p className="text-amber-700 mt-0.5">
                This pursuit has a work-scope document on file, but Build Matrix hasn&rsquo;t been run on it.
                The statement will be generated from your Client Library alone, without the PWS/SOW requirement
                text to ground it against.
              </p>
              <Link
                to={`/${workspaceId}/pursuit/${pursuitId}/intake`}
                className="inline-flex items-center gap-1 mt-1.5 text-amber-900 font-medium hover:underline"
              >
                Go build it first <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="auto" data-testid="cap-stmt-tab-auto">Auto-pick best matches</TabsTrigger>
            <TabsTrigger value="directed" data-testid="cap-stmt-tab-directed">Choose records</TabsTrigger>
          </TabsList>

          <TabsContent value="auto" className="flex-1 min-h-0 overflow-y-auto pt-3">
            <AutoSummary library={library} loading={loading} />
          </TabsContent>

          <TabsContent value="directed" className="flex-1 min-h-0 overflow-hidden pt-3">
            <DirectedPicker
              library={library}
              loading={loading}
              picked={picked}
              onToggle={togglePick}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 sm:gap-2 pt-3">
          <Button variant="outline" onClick={onClose} disabled={generating}>Cancel</Button>
          <Button
            onClick={onGenerate}
            disabled={generating || (tab === 'directed' && !directedReady)}
            className="bg-violet-700 hover:bg-violet-800"
            data-testid="cap-stmt-gen-confirm-btn"
          >
            {generating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-1.5" />
                Generate
                {tab === 'directed' && totalSelected > 0 && ` (${totalSelected})`}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AutoSummary({ library, loading }) {
  if (loading) {
    return <div className="space-y-2"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>;
  }
  if (!library) return null;
  const counts = {
    past_performances: library.past_performances?.length || 0,
    certifications: library.certifications?.length || 0,
    key_personnel: library.key_personnel?.length || 0,
    winning_proposals: library.winning_proposals?.length || 0,
  };
  const total = Object.values(counts).reduce((s, n) => s + n, 0);

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        We&rsquo;ll score every record in your Client Library against this RFI&rsquo;s NAICS, agency,
        keywords, set-aside, recency &amp; CPARS, then feed the top matches to the AI.
      </p>
      {total === 0 ? (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 text-sm text-amber-800">
            Your Client Library is empty. Add Past Performances, Certifications, Key Personnel,
            or Winning Proposals from the Client Profile first.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(counts).map(([key, n]) => {
            const Icon = SECTION_ICONS[key];
            return (
              <div key={key} className="border border-slate-200 rounded-md p-3 text-center">
                <Icon className="w-5 h-5 mx-auto text-violet-600 mb-1" />
                <div className="text-2xl font-semibold text-slate-900">{n}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500">
                  {SECTION_LABELS[key]}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {library.signals && (
        <div className="text-xs text-slate-500 border border-slate-100 bg-slate-50 rounded-md p-2 flex flex-wrap gap-x-3 gap-y-1">
          <span>NAICS: <strong>{library.signals.naics || '—'}</strong></span>
          <span>Agency: <strong>{library.signals.agency || '—'}</strong></span>
          <span>Set-aside: <strong>{library.signals.set_aside || '—'}</strong></span>
          <span>Keywords: <strong>{library.signals.keyword_count}</strong></span>
          {library.requirements_matrix?.built && (
            <span>Requirements: <strong>{library.requirements_matrix.requirement_count}</strong></span>
          )}
        </div>
      )}
    </div>
  );
}

function DirectedPicker({ library, loading, picked, onToggle }) {
  if (loading) {
    return <div className="space-y-2"><Skeleton className="h-32" /><Skeleton className="h-32" /></div>;
  }
  if (!library) return null;

  const renderRow = (kind, item, displayFn) => {
    const idKey = {
      past_performances: 'past_performance_ids',
      certifications: 'certification_ids',
      key_personnel: 'key_personnel_ids',
      winning_proposals: 'winning_proposal_ids',
    }[kind];
    const checked = picked[idKey].includes(item.id);
    return (
      <label
        key={`${kind}-${item.id}`}
        className="flex items-start gap-2 p-2 border border-slate-100 hover:bg-slate-50 rounded cursor-pointer"
      >
        <Checkbox
          checked={checked}
          onCheckedChange={() => onToggle(idKey, item.id)}
          className="mt-0.5"
          data-testid={`cap-stmt-pick-${kind}-${item.id}`}
        />
        <div className="flex-1 min-w-0">
          {displayFn(item)}
        </div>
      </label>
    );
  };

  return (
    <ScrollArea className="h-[420px] pr-3">
      <div className="space-y-4">
        <PickerSection
          icon={Briefcase}
          label="Past Performance"
          count={library.past_performances?.length || 0}
        >
          {(library.past_performances || []).map(pp => renderRow('past_performances', pp, item => (
            <>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-medium text-sm text-slate-900">{item.title}</span>
                {item.score > 0 && (
                  <Badge variant="outline" className={`text-[10px] ${_scoreColor(item.score)}`}>
                    {item.score}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {item.agency || '—'}
                {item.contract_value && ` · $${Number(item.contract_value).toLocaleString()}`}
                {item.cpars_rating && ` · CPARS ${item.cpars_rating}`}
              </div>
              {item.match_reasons?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {item.match_reasons.slice(0, 3).map((r, i) => (
                    <Badge key={`${r}-${i}`} variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                      {r}
                    </Badge>
                  ))}
                </div>
              )}
            </>
          )))}
        </PickerSection>

        <PickerSection
          icon={Award}
          label="Certifications"
          count={library.certifications?.length || 0}
        >
          {(library.certifications || []).map(c => renderRow('certifications', c, item => (
            <>
              <div className="font-medium text-sm text-slate-900">{item.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {item.category || '—'}
                {item.issuer && ` · ${item.issuer}`}
                {item.expiration_date && ` · expires ${(item.expiration_date || '').slice(0, 10)}`}
              </div>
            </>
          )))}
        </PickerSection>

        <PickerSection
          icon={Users}
          label="Key Personnel"
          count={library.key_personnel?.length || 0}
        >
          {(library.key_personnel || []).map(kp => renderRow('key_personnel', kp, item => (
            <>
              <div className="font-medium text-sm text-slate-900">
                {item.first_name} {item.last_name}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {item.title || '—'}
                {item.years_experience ? ` · ${item.years_experience}y exp` : ''}
                {item.clearance ? ` · ${item.clearance}` : ''}
              </div>
            </>
          )))}
        </PickerSection>

        <PickerSection
          icon={Trophy}
          label="Winning Proposals"
          count={library.winning_proposals?.length || 0}
        >
          {(library.winning_proposals || []).map(wp => renderRow('winning_proposals', wp, item => (
            <>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-medium text-sm text-slate-900">{item.title}</span>
                {item.score > 0 && (
                  <Badge variant="outline" className={`text-[10px] ${_scoreColor(item.score)}`}>
                    {item.score}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {item.agency || '—'}
                {item.contract_value && ` · $${Number(item.contract_value).toLocaleString()}`}
                {item.naics_code && ` · NAICS ${item.naics_code}`}
              </div>
            </>
          )))}
        </PickerSection>
      </div>
    </ScrollArea>
  );
}

function PickerSection({ icon: Icon, label, count, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white z-10 pb-1 border-b border-slate-100">
        <Icon className="w-4 h-4 text-violet-700" />
        <h4 className="text-sm font-semibold text-slate-900">{label}</h4>
        <Badge variant="outline" className="text-[10px]">{count}</Badge>
      </div>
      {count === 0 ? (
        <div className="text-xs text-slate-400 italic px-2 py-1">No records in library.</div>
      ) : (
        <div className="space-y-1">{children}</div>
      )}
    </div>
  );
}

export default CapabilityStatementPage;
